<script setup lang="ts">
import type { BackendError, Playlist, APIErrorResponse } from '~/types'

import { sortBy } from 'lodash-es'
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import Modal from '~/ui/Modal.vue'
import PlaylistForm from '~/components/playlists/Form.vue'
import useLogger from '~/composables/useLogger'
import { useStore } from '~/store'
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { generateTrackCreditString } from '~/utils/utils'
import { dialog, to } from '~/routing'
import { useDataStore } from '~/data'

import Button from '~/ui/Button.vue'
import Link from '~/ui/Link.vue'
import Alert from '~/ui/Alert.vue'
import Spacer from '~/ui/Spacer.vue'
import Input from '~/ui/Input.vue'
import Table from '~/ui/Table.vue'

const logger = useLogger()
const store = useStore()

const showDuplicateTrackAddConfirmation = ref(false)

const router = useRouter()
router.beforeEach(() => {
  store.commit('playlists/showModal', false)
  showDuplicateTrackAddConfirmation.value = false
})

const playlists = computed(() => store.state.playlists.playlists)
const track = computed(() => store.state.playlists.modalTrack)

const trackArtists = computed(() => useDataStore().getMany('artist', () => (track.value?.artist_credit ?? []).map(credit => credit.artist.id)).value)

const trackCreditString = computed(() => generateTrackCreditString(track.value?.artist_credit ?? [], trackArtists.value))

const { t } = useI18n()
const labels = computed(() => ({
  addToPlaylist: t('components.playlists.PlaylistModal.button.addToPlaylist'),
  filterPlaylistField: t('components.playlists.PlaylistModal.placeholder.filterPlaylist')
}))

const playlistNameFilter = ref('')

const sortedPlaylists = computed(() =>
  sortBy(
    playlists.value.filter((playlist: Playlist) => playlist.name.match(new RegExp(playlistNameFilter.value, 'i')) !== null),
    (playlist: Playlist) => playlist.modification_date
  ).reverse()
)

const formKey = ref(new Date().toString())
watch(() => store.state.playlists.showModal, () => {
  formKey.value = new Date().toString()
  showDuplicateTrackAddConfirmation.value = false
})

// Sync with links to `dialog.to('create', 'playlist')`:
// the URL flag opens the modal, and closing the modal clears the flag.
// Opening via `playlists/chooseTrack` stays store-only and never touches the URL.
const urlFlag = dialog.model('create', 'playlist')
watch(() => urlFlag.modelValue, isOpen => {
  if (isOpen) store.commit('playlists/showModal', true)
}, { immediate: true })
watch(() => store.state.playlists.showModal, isOpen => {
  if (!isOpen && urlFlag.modelValue) urlFlag['onUpdate:modelValue'](false)
})

const lastSelectedPlaylist = ref('')
const errors = ref([] as string[])
const duplicateTrackAddInfo = ref({} as { playlist_name?: string })

const addToPlaylist = async (playlistId: string, allowDuplicates: boolean) => {
  lastSelectedPlaylist.value = playlistId

  try {
    await axios.post(`playlists/${playlistId}/add/`, {
      tracks: [track.value?.id].filter(i => i),
      allow_duplicates: allowDuplicates
    })

    logger.info('Successfully added track to playlist')
    store.state.playlists.showModal = false
    store.dispatch('playlists/fetchOwn')
  } catch (error) {
    if (error as BackendError) {
      const { backendErrors, rawPayload = {} } = error as BackendError

      if (backendErrors.length === 1 && backendErrors[0] === 'Tracks Already Exist In Playlist') {
        duplicateTrackAddInfo.value = ((rawPayload.playlist as APIErrorResponse).non_field_errors as APIErrorResponse)[0] as object
        showDuplicateTrackAddConfirmation.value = true
      } else {
        errors.value = backendErrors
        showDuplicateTrackAddConfirmation.value = false
      }
    }
  }
}

store.dispatch('playlists/fetchOwn')
</script>

<template>
  <Modal
    v-model="store.state.playlists.showModal"
    :title="t('components.playlists.PlaylistModal.header.addToPlaylist')"
    :cancel="t('components.playlists.PlaylistModal.button.cancel')"
    :priority="8"
  >
    <template v-if="track">
      <h3 class="ui header">
        {{ t('components.playlists.PlaylistModal.header.addToPlaylist') }}
        <div class="ui sub header">
          {{ t('components.playlists.PlaylistModal.header.track', { artist: trackCreditString, title: track.title }) }}
        </div>
      </h3>
    </template>
    <div v-if="playlists.length > 0">
      <Alert
        v-if="showDuplicateTrackAddConfirmation" class="color-yellow-solid"
      >
        <p>
          <i18n-t keypath="components.playlists.PlaylistModal.warning.duplicate">
            <strong>{{ track?.title }}</strong>
            <strong>{{ duplicateTrackAddInfo.playlist_name }}</strong>
          </i18n-t>
        </p>
        <Button
          class="color-primary-solid"
          @click="addToPlaylist(lastSelectedPlaylist, true)"
        >
          {{ t('components.playlists.PlaylistModal.button.addDuplicate') }}
        </Button>
      </Alert>
      <Alert
        v-if="errors.length > 0"
        role="alert"
        red
      >
        <h4 class="header">
          {{ t('components.playlists.PlaylistModal.header.addFailure') }}
        </h4>
        <ul class="list">
          <li
            v-for="(error, key) in errors"
            :key="key"
          >
            {{ error }}
          </li>
        </ul>
      </Alert>

      <h4 class="ui header">
        {{ t('components.playlists.PlaylistModal.header.available') }}
      </h4>
      <Input
        id="playlist-name-filter"
        v-model="playlistNameFilter"
        :placeholder="labels.filterPlaylistField"
        :label="t('components.playlists.PlaylistModal.label.filter')"
      />
      <Table
        v-if="sortedPlaylists.length > 0"
        :grid-template-columns="track ? ['48px', '1fr', 'auto', 'auto', 'auto'] : ['48px', '1fr', 'auto', 'auto']"
      >
        <template #header>
          <label><span class="visually-hidden">{{ t('components.playlists.PlaylistModal.table.edit.header.edit') }}</span></label>
          <label>{{ t('components.playlists.PlaylistModal.table.edit.header.name') }}</label>
          <label>{{ t('components.playlists.PlaylistModal.table.edit.header.lastModification') }}</label>
          <label>{{ t('components.playlists.PlaylistModal.table.edit.header.tracks') }}</label>
          <label v-if="track" />
        </template>

        <template
          v-for="(playlist, key) in sortedPlaylists"
          :key="key"
        >
          <div>
            <Link
              class="color-default-solid shape-square"
              ui-block-size="40"
              ui-raised
              v-bind="to({ name: 'library.playlists.detail', params: { id: playlist.uuid }, query: { mode: 'edit' } })"
            >
              <Icon icon="bi:pencil-fill" />
              <span class="visually-hidden">{{ t('components.playlists.PlaylistModal.button.edit') }}</span>
            </Link>
          </div>
          <div>
            <!-- TODO: Use Link component here -->
            <router-link
              :to="{ name: 'library.playlists.detail', params: { id: playlist.uuid } }"
              @click="store.state.playlists.showModal = false"
            >
              {{ playlist.name }}
            </router-link>
          </div>
          <div><human-date :date="playlist.modification_date" /></div>
          <div>{{ playlist.tracks_count }}</div>
          <div v-if="track">
            <Button
              ui-block-size="40"
              class="color-primary-solid"
              :title="labels.addToPlaylist"
              @click.prevent="addToPlaylist(playlist.uuid, false)"
            >
              <Icon icon="bi:plus" />
              {{ t('components.playlists.PlaylistModal.button.addTrack') }}
            </Button>
          </div>
        </template>
      </Table>
      <template v-else>
        <Spacer />
        <Alert class="color-blue-solid">
          <span>
            {{ t('components.playlists.PlaylistModal.header.noResults') }}
          </span>
        </Alert>
      </template>
    </div>
    <div
      v-else
      class="ui placeholder segment"
    >
      <div class="ui icon header">
        <Icon icon="bi:list" />
        {{ t('components.playlists.PlaylistModal.empty.noPlaylists') }}
      </div>
    </div>

    <Spacer />

    <playlist-form
      :key="formKey"
      :create="true"
    />
  </Modal>
</template>
